import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, Pencil, Trash2, ArrowUpDown } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

export default function Records() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const readOnly = user?.role !== "admin"; // HOD: view & download only
  const [filters, setFilters] = useState({ department_id: "all", semester_id: "all", subject_id: "all", date_from: "", date_to: "" });
  const [viewRecord, setViewRecord] = useState(null);
  const [viewStudents, setViewStudents] = useState([]);
  const [editRecord, setEditRecord] = useState(null);
  const [editStatuses, setEditStatuses] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/admin/departments")).data });
  const { data: semesters = [] } = useQuery({ queryKey: ["semesters"], queryFn: async () => (await api.get("/admin/semesters")).data });
  const { data: subjects = [] } = useQuery({ queryKey: ["subjects"], queryFn: async () => (await api.get("/admin/subjects")).data });

  const params = () => {
    const p = {};
    if (filters.department_id !== "all") p.department_id = filters.department_id;
    if (filters.semester_id !== "all") p.semester_id = filters.semester_id;
    if (filters.subject_id !== "all") p.subject_id = filters.subject_id;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  };

  const { data: records = [] } = useQuery({
    queryKey: ["admin-attendance", filters],
    queryFn: async () => (await api.get("/admin/attendance", { params: params() })).data,
  });

  const [sort, setSort] = useState({ key: "roll_number", dir: 1 });
  const { data: summary = [] } = useQuery({
    queryKey: ["admin-summary", filters],
    queryFn: async () => (await api.get("/admin/reports/summary", { params: params() })).data,
  });
  const sortedSummary = [...summary].sort((a, b) => {
    const va = a[sort.key];
    const vb = b[sort.key];
    return (typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb))) * sort.dir;
  });

  const queryString = () => {
    const qs = new URLSearchParams(params()).toString();
    return qs ? `?${qs}` : "";
  };

  const openView = async (rec) => {
    const { data } = await api.get(`/admin/attendance/${rec.id}/students`);
    setViewStudents(data);
    setViewRecord(rec);
  };

  const openEdit = async (rec) => {
    const { data } = await api.get(`/admin/attendance/${rec.id}/students`);
    const statuses = {};
    data.forEach((s) => { statuses[s.student_id] = s.status; });
    setEditStatuses(statuses);
    setEditRecord(rec);
    setViewStudents(data);
  };

  const saveEdit = async () => {
    const present_ids = Object.keys(editStatuses).filter((id) => editStatuses[id] === "present");
    const absent_ids = Object.keys(editStatuses).filter((id) => editStatuses[id] === "absent");
    try {
      await api.put(`/admin/attendance/${editRecord.id}`, { present_ids, absent_ids });
      toast.success("Attendance record updated");
      setEditRecord(null);
      queryClient.invalidateQueries({ queryKey: ["admin-attendance"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/attendance/${deleteId}`);
      toast.success("Record deleted");
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ["admin-attendance"] });
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const FilterSelect = ({ value, onChange, options, placeholder, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const StatusBadge = ({ status }) => (
    <Badge variant="outline" className={status === "present"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-red-50 text-red-700 border-red-200"}>
      {status}
    </Badge>
  );

  return (
    <Layout title="Attendance Records">
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="records-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
          <div className="space-y-1.5"><Label>Department</Label>
            <FilterSelect testid="filter-department" value={filters.department_id}
              onChange={(v) => setFilters({ ...filters, department_id: v, semester_id: "all", subject_id: "all" })}
              options={departments.map((d) => ({ value: d.id, label: d.name }))} placeholder="Department" /></div>
          <div className="space-y-1.5"><Label>Semester</Label>
            <FilterSelect testid="filter-semester" value={filters.semester_id}
              onChange={(v) => setFilters({ ...filters, semester_id: v, subject_id: "all" })}
              options={semesters.filter((s) => filters.department_id === "all" || s.department_id === filters.department_id).map((s) => ({ value: s.id, label: s.name }))} placeholder="Semester" /></div>
          <div className="space-y-1.5"><Label>Subject</Label>
            <FilterSelect testid="filter-subject" value={filters.subject_id}
              onChange={(v) => setFilters({ ...filters, subject_id: v })}
              options={subjects.filter((s) => (filters.semester_id === "all" || s.semester_id === filters.semester_id) && (filters.department_id === "all" || s.department_id === filters.department_id)).map((s) => ({ value: s.id, label: s.name }))} placeholder="Subject" /></div>
          <div className="space-y-1.5"><Label>From</Label>
            <Input type="date" data-testid="filter-date-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>To</Label>
            <Input type="date" data-testid="filter-date-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" data-testid="download-csv-btn"
              onClick={() => downloadFile(`/admin/reports/attendance.csv${queryString()}`, "attendance_report.csv")}>
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" data-testid="download-excel-btn"
              onClick={() => downloadFile(`/admin/reports/attendance.xlsx${queryString()}`, "attendance_report.xlsx")}>
              <Download className="h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="download-pdf-btn"
              onClick={() => downloadFile(`/admin/reports/attendance.pdf${queryString()}`, "attendance_report.pdf")}>
              <Download className="h-4 w-4" /> PDF
            </Button>
            <Button variant="outline" data-testid="download-summary-excel-btn"
              onClick={() => downloadFile(`/admin/reports/summary.xlsx${queryString()}`, "attendance_summary.xlsx")}>
              <Download className="h-4 w-4" /> Summary Excel
            </Button>
            <Button variant="outline" data-testid="download-summary-pdf-btn"
              onClick={() => downloadFile(`/admin/reports/summary.pdf${queryString()}`, "attendance_summary.pdf")}>
              <Download className="h-4 w-4" /> Summary PDF
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="sessions" data-testid="records-tabs">
        <TabsList className="bg-white border border-slate-200 mb-6">
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Student Summary</TabsTrigger>
        </TabsList>
        <TabsContent value="summary">
          <div className="bg-white border border-slate-200 rounded-md" data-testid="summary-card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">Student attendance summary</h2>
              <p className="text-sm text-slate-500 mt-0.5">Per-student totals for the filters above. Click a column header to sort.</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    ["Semester", "semester_name", ""],
                    ["Name", "name", ""],
                    ["Roll Number", "roll_number", ""],
                    ["Total Classes", "total_classes", "text-right"],
                    ["Attended", "attended", "text-right"],
                    ["Attendance %", "percentage", "text-right"],
                  ].map(([label, k, cls]) => (
                    <TableHead key={k} className={cls}>
                      <button data-testid={`sort-${k}`}
                        onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}
                        className={`inline-flex items-center gap-1 hover:text-slate-950 ${cls === "text-right" ? "flex-row-reverse" : ""}`}>
                        {label} <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSummary.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10" data-testid="summary-empty">
                    No attendance data for the selected filters.
                  </TableCell></TableRow>
                )}
                {sortedSummary.map((r) => (
                  <TableRow key={r.student_id} data-testid={`summary-row-${r.student_id}`}>
                    <TableCell>{r.semester_name}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="font-data">{r.roll_number}</TableCell>
                    <TableCell className="text-right font-data">{r.total_classes}</TableCell>
                    <TableCell className="text-right font-data">{r.attended}</TableCell>
                    <TableCell className={`text-right font-data font-semibold ${r.percentage < 70 ? "text-red-700" : "text-emerald-700"}`}
                      data-testid={`summary-pct-${r.student_id}`}>
                      {r.percentage}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="sessions">
      <div className="bg-white border border-slate-200 rounded-md" data-testid="records-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Semester</TableHead>
              <TableHead className="text-right">Present</TableHead>
              <TableHead className="text-right">Absent</TableHead>
              <TableHead>Marked by</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10" data-testid="records-empty">
                No attendance records match these filters.
              </TableCell></TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`record-row-${r.id}`}>
                <TableCell className="font-data">{r.date}</TableCell>
                <TableCell>{r.period_label}</TableCell>
                <TableCell>{r.subject_name}</TableCell>
                <TableCell>{r.semester_name}</TableCell>
                <TableCell className="text-right font-data text-emerald-700">{r.present_ids?.length ?? 0}</TableCell>
                <TableCell className="text-right font-data text-red-700">{r.absent_ids?.length ?? 0}</TableCell>
                <TableCell className="text-slate-500">{r.marked_by_name}{r.edited_by ? " (edited)" : ""}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" data-testid={`view-record-${r.id}`} onClick={() => openView(r)}
                      className="text-slate-400 hover:text-slate-950 hover:bg-slate-100"><Eye className="h-4 w-4" /></Button>
                    {!readOnly && (<>
                      <Button variant="ghost" size="icon" data-testid={`edit-record-${r.id}`} onClick={() => openEdit(r)}
                        className="text-slate-400 hover:text-slate-950 hover:bg-slate-100"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" data-testid={`delete-record-${r.id}`} onClick={() => setDeleteId(r.id)}
                        className="text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                    </>)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!viewRecord} onOpenChange={() => setViewRecord(null)}>
        <DialogContent className="bg-white rounded-md max-w-2xl" data-testid="view-record-dialog">
          <DialogHeader>
            <DialogTitle>{viewRecord?.subject_name} — {viewRecord?.date} · {viewRecord?.period_label}</DialogTitle>
          </DialogHeader>
          {viewRecord?.topic ? (
            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="view-record-topic">
              <span className="font-medium text-slate-950">Topic taught:</span> {viewRecord.topic}
            </p>
          ) : null}
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Roll No</TableHead><TableHead>Student</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {viewStudents.map((s) => (
                  <TableRow key={s.student_id} data-testid={`view-student-${s.student_id}`}>
                    <TableCell className="font-data">{s.roll_number}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRecord} onOpenChange={() => setEditRecord(null)}>
        <DialogContent className="bg-white rounded-md max-w-2xl" data-testid="edit-record-dialog">
          <DialogHeader>
            <DialogTitle>Edit — {editRecord?.subject_name} · {editRecord?.date} · {editRecord?.period_label}</DialogTitle>
          </DialogHeader>
          {editRecord?.topic ? (
            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="edit-record-topic">
              <span className="font-medium text-slate-950">Topic taught:</span> {editRecord.topic}
            </p>
          ) : null}
          <div className="max-h-80 overflow-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Roll No</TableHead><TableHead>Student</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {viewStudents.map((s) => (
                  <TableRow key={s.student_id}>
                    <TableCell className="font-data">{s.roll_number}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <button
                        data-testid={`edit-toggle-${s.student_id}`}
                        onClick={() => setEditStatuses({ ...editStatuses, [s.student_id]: editStatuses[s.student_id] === "present" ? "absent" : "present" })}
                        className="active:scale-95 transition-transform duration-150">
                        <StatusBadge status={editStatuses[s.student_id] || s.status} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button onClick={saveEdit} data-testid="save-edit-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
            Save changes
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white rounded-md" data-testid="delete-record-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this attendance record?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the record. Student percentages will be recalculated.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
